import { MainButton } from "@/Components/MainButton"
import { setModalData } from "@/store/globalSlice"
import { useAppDispatch, useAppSelector } from "@/store/hooks"

interface GameEndStatsButtonProps {}

/**
 * Sits in the board's button slot once the game is over (GameButtons renders
 * nothing then) and re-opens the end-of-game screen the player was already
 * shown, so closing it isn't a one-way door.
 *
 * Which screen that is depends on how the game ended — a bankruptcy win gets
 * GameEndModalWithoutStats, a game that ran its course gets EndGameModal. Both
 * modals stay mounted and self-guard on their modalData flag, so flipping the
 * flag is all this has to do.
 */
const GameEndStatsButton: React.FC<GameEndStatsButtonProps> = () => {
  const dispatch = useAppDispatch()
  const gameOver = useAppSelector(
    (state) => state.global.myGame?.situation?.gameOver
  )
  const gameEndType = useAppSelector((state) => state.global.gameEndType)
  const situationPlayers = useAppSelector(
    (state) => state.global.myGame?.situation?.players
  )

  if (!gameOver) return null

  // gameEndType is recorded from the winner-announce socket event. A client that
  // wasn't listening for it (reconnected into an already-finished game) still
  // needs the right screen, so fall back to the same thing the server checks:
  // one survivor means everyone else was bankrupted out.
  const endType =
    gameEndType ??
    ((situationPlayers?.filter((player) => !player.isBankrupt).length === 1
      ? "bankruptcy"
      : "standings") as IGameEndType)

  const handleClick = () => {
    dispatch(
      setModalData(
        endType === "bankruptcy"
          ? { gameEndWithoutStats: { visible: true } }
          : { endGame: { visible: true } }
      )
    )
  }

  return (
    <MainButton
      baseColor="#c9962e"
      size="base"
      onClick={handleClick}
      data-testid="game.show-stats-btn"
    >
      <div className="flex flex-row items-center justify-center gap-3 w-full">
        <i className="fa-trophy fa-solid text-white fa-xl"></i>
        <span className="text-base text-white/90 font-poppins font-semibold tracking-wide uppercase">
          Show stats
        </span>
      </div>
    </MainButton>
  )
}

export default GameEndStatsButton
